import { IconDownload } from '@tabler/icons-react';
import useTheme from 'chat-ui/src/hooks/useTheme';
import useImageViewerStore from 'chat-ui/src/store/ImageViewerStore';
import DOMPurify from 'isomorphic-dompurify';
import dayjs from 'dayjs';
import { MessageAtachment } from '@common/ts/types';
import { cn } from '@/lib/utils';
import AudioMessage from './audio/AudioMessage';
import VideoMessage from './video/VideoMessage';
import { EMessageType } from '@/enums';
import MessageContentParser from './MessageContentParser';
import { TranscriptsMessages } from '@/types';

function ConversationMessage({ role, data, text, created_at }: TranscriptsMessages) {
  const { currentTheme } = useTheme();
  const { setIsViewerOpen, setCurrentImageUrl, setCurrentImageAlt, setImages, setCurrentIndex } =
    useImageViewerStore();
  const sanitizedText = DOMPurify.sanitize(text || '');

  // Helper: Render download button
  const renderDownloadButton = (item: MessageAtachment, downloadName: string, iconSize: number) => (
    <a
      style={{ color: currentTheme?.primary }}
      className={cn(
        role === 'user' ? '-left-6' : '-right-6',
        'absolute hover:shadow-xl p-1 top-2 bg-white border rounded-full shadow z-10',
      )}
      href={item.url}
      download={downloadName}
    >
      <IconDownload size={iconSize} />
    </a>
  );

  // Helper: Render media container with content and optional label
  const renderMediaContainer = (content: React.ReactNode, item: MessageAtachment) => (
    <div
      className={cn(
        'w-full rounded-lg overflow-hidden',
        role === 'user' ? 'bg-transparent' : 'bg-white',
      )}
    >
      {content}
      {item.label && (
        <div className="w-full text-xs p-2 text-center text-gray-700 font-medium break-words">
          {item.label}
        </div>
      )}
    </div>
  );

  // Render audio items (full width)
  const renderAudioItems = () => {
    const audioItems =
      data?.filter((item: MessageAtachment) => item.type === EMessageType.audio) || [];
    if (audioItems.length === 0) return null;

    return audioItems.map((item: MessageAtachment, index: number) => {
      const itemKey = item.file_id || item.url || `audio-${index}`;
      return (
        <div key={itemKey} className="relative">
          {renderDownloadButton(item, 'tellet-audio', 16)}
          {renderMediaContainer(
            <AudioMessage
              isUser={role === 'user'}
              url={item.url}
              transcript={item.transcription_text || ''}
            />,
            item,
          )}
        </div>
      );
    });
  };

  // Get image items for the viewer
  const imageItems =
    data?.filter((item: MessageAtachment) => item.type === EMessageType.image) || [];

  // Helper: Show image in fullscreen viewer
  const showImagePreview = (url: string, label: string) => {
    const images = imageItems.map((img: MessageAtachment) => ({ url: img.url, label: img.label }));
    setImages(images);
    const index = images.findIndex((img: { url: string }) => img.url === url);
    setCurrentIndex(index >= 0 ? index : 0);
    setCurrentImageUrl(url);
    setCurrentImageAlt(label || 'image preview');
    setIsViewerOpen(true);
  };

  // Render image items (grid for multiple, full width for single)
  const renderImageItems = () => {
    if (imageItems.length === 0) return null;

    // Single image: render full width without grid
    if (imageItems.length === 1) {
      const item = imageItems[0];
      const itemKey = item.file_id || item.url || 'image-0';
      return (
        <div key={itemKey} className="relative">
          {renderDownloadButton(item, 'tellet-image', 17)}
          {renderMediaContainer(
            <img
              src={item.url}
              alt={item.label || ''}
              className="w-full h-auto object-cover cursor-zoom-in hover:opacity-90 transition-opacity"
              onClick={() => showImagePreview(item.url, item.label || '')}
            />,
            item,
          )}
        </div>
      );
    }

    // Multiple images: use grid layout
    return (
      <div
        className={cn(
          'grid gap-2 justify-center',
          imageItems.length === 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3',
        )}
      >
        {imageItems.map((item: MessageAtachment, index: number) => {
          const itemKey = item.file_id || item.url || `image-${index}`;
          return (
            <div
              key={itemKey}
              className="min-h-[22px] flex flex-col justify-center md:justify-start relative"
            >
              {renderDownloadButton(item, 'tellet-image', 17)}
              {renderMediaContainer(
                <img
                  src={item.url}
                  alt={item.label || ''}
                  className="w-full h-auto object-cover cursor-zoom-in hover:opacity-90 transition-opacity"
                  onClick={() => showImagePreview(item.url, item.label || '')}
                />,
                item,
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Render video items (always full width, stacked vertically for readable transcripts)
  const renderVideoItems = () => {
    const videoItems =
      data?.filter((item: MessageAtachment) => item.type === EMessageType.video) || [];
    if (videoItems.length === 0) return null;

    return videoItems.map((item: MessageAtachment, index: number) => {
      const itemKey = item.file_id || item.url || `video-${index}`;
      return (
        <div key={itemKey} className="relative">
          {renderDownloadButton(item, 'tellet-video', 17)}
          {renderMediaContainer(
            <VideoMessage url={item.url} transcript={item.transcription_text || ''} />,
            item,
          )}
        </div>
      );
    });
  };

  return (
    <div className="flex flex-col gap-1">
      <div
        style={{
          backgroundColor:
            (role === 'assistant' && '#FFFFFF') || (role === 'user' && currentTheme?.primary) || '',
        }}
        className={cn(
          'max-w-xl',
          role === 'assistant' &&
            'self-start border rounded-lg mr-[52px] min450:mr-0 px-4 py-2 text-[15px] shadow-sm text-dark-text border-dark-text-10',
          role === 'user' &&
            'border self-end rounded-lg px-4 py-2 ml-[52px] min450:ml-0 text-[15px] shadow-sm text-white border-dark-text/10"',
        )}
      >
        <div
          className={cn(
            'flex gap-4 flex-col',
            role === 'assistant' && 'justify-start',
            role === 'user' && 'justify-end',
          )}
        >
          {/* Text content (full width) */}
          {role === 'assistant' ? (
            <MessageContentParser
              content={sanitizedText}
              className="[&_a]:underline [&_a]:text-blue-400"
            />
          ) : (
            /*  eslint-disable-next-line react/no-danger */
            <p
              className="[&_a]:underline [&_a]:text-blue-400"
              dangerouslySetInnerHTML={{ __html: sanitizedText as string }}
            />
          )}

          {/* Audio items (full width) */}
          {renderAudioItems()}

          {/* Image items (grid for multiple, full width for single) */}
          {renderImageItems()}

          {/* Video items (always full width for readable transcripts) */}
          {renderVideoItems()}
        </div>
      </div>
      <p
        className={cn(
          role === 'user' ? 'justify-end pr-1' : 'justify-start pl-1',
          'flex w-full text-black font-medium text-[10px]',
        )}
      >
        {dayjs(created_at).format('hh:mm:ss A')}
      </p>
    </div>
  );
}

export default ConversationMessage;
